/**
 * デザインシステム - テーマ定数
 *
 * プロジェクト全体で使用するTailwindCSSクラスの定数定義。
 * コンポーネントの一貫性を保つために使用する。
 *
 * 使用例:
 *   import { btn, card, input, badge } from '@/lib/theme'
 *   <button className={btn.primary}>送信</button>
 */

// ============================================================
// カラーパレット
// ============================================================
export const colors = {
  primary: {
    bg: 'bg-blue-600',
    bgHover: 'hover:bg-blue-700',
    bgLight: 'bg-blue-50 dark:bg-blue-900/30',
    bgLightHover: 'hover:bg-blue-100 dark:hover:bg-blue-800/40',
    text: 'text-blue-600 dark:text-blue-400',
    textDark: 'text-blue-700 dark:text-blue-300',
    textHover: 'hover:text-blue-600 dark:hover:text-blue-400',
    border: 'border-blue-200 dark:border-blue-700',
    ring: 'focus:ring-blue-500',
  },
  success: {
    bg: 'bg-green-600',
    bgHover: 'hover:bg-green-700',
    bgLight: 'bg-green-50 dark:bg-green-900/30',
    bgLightHover: 'hover:bg-green-50 dark:hover:bg-green-800/40',
    text: 'text-green-600 dark:text-green-400',
    textDark: 'text-green-700 dark:text-green-300',
    textHover: 'hover:text-green-600 dark:hover:text-green-400',
    border: 'border-green-200 dark:border-green-700',
  },
  warning: {
    bg: 'bg-yellow-500',
    bgLight: 'bg-yellow-50 dark:bg-yellow-900/30',
    text: 'text-yellow-600 dark:text-yellow-400',
    textDark: 'text-yellow-700 dark:text-yellow-300',
    border: 'border-yellow-200 dark:border-yellow-700',
  },
  danger: {
    bg: 'bg-red-600',
    bgHover: 'hover:bg-red-700',
    bgLight: 'bg-red-50 dark:bg-red-900/30',
    bgLightHover: 'hover:bg-red-50 dark:hover:bg-red-800/40',
    text: 'text-red-600 dark:text-red-400',
    textDark: 'text-red-700 dark:text-red-300',
    textHover: 'hover:text-red-700 dark:hover:text-red-300',
    border: 'border-red-200 dark:border-red-700',
  },
  info: {
    bgLight: 'bg-blue-50 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    textDark: 'text-blue-800 dark:text-blue-200',
    border: 'border-blue-200 dark:border-blue-700',
  },
  neutral: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    bgHover: 'hover:bg-gray-50 dark:hover:bg-gray-700',
    bgDark: 'bg-gray-200 dark:bg-gray-600',
    text: 'text-gray-600 dark:text-gray-400',
    textDark: 'text-gray-700 dark:text-gray-300',
    textDarker: 'text-gray-900 dark:text-gray-100',
    textLight: 'text-gray-500 dark:text-gray-400',
    textLighter: 'text-gray-400 dark:text-gray-500',
    border: 'border-gray-200 dark:border-gray-700',
    borderDark: 'border-gray-300 dark:border-gray-600',
  },
} as const

// ============================================================
// ボタン
// ============================================================
export const btn = {
  // ベーススタイル（全ボタン共通）
  base: 'inline-flex items-center justify-center gap-2 font-medium transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed',

  // プライマリボタン
  primary: 'inline-flex items-center justify-center gap-2 font-medium transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm',

  // セカンダリボタン
  secondary: 'inline-flex items-center justify-center gap-2 font-medium transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 text-sm',

  // 危険操作ボタン（テキストのみ）
  danger: 'inline-flex items-center justify-center gap-2 font-medium transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 px-4 py-2 text-sm',

  // 危険操作ボタン（塗りつぶし）
  dangerFill: 'inline-flex items-center justify-center gap-2 font-medium transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm',

  // アイコンボタン
  icon: 'p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  iconBlue: 'p-2 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30',
  iconGreen: 'p-2 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30',
  iconRed: 'p-2 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30',
  iconPurple: 'p-2 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30',

  // サイズバリアント
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-2.5 text-base',
} as const

// ============================================================
// カード
// ============================================================
export const card = {
  // 標準カード（シャドウあり）
  base: 'bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6',
  // ホバー時シャドウ
  hover: 'bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6 hover:shadow-lg transition-shadow',
  // ボーダーカード（シャドウなし）
  bordered: 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6',
  // 小さいカード
  sm: 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4',
  // テーブルラッパー
  table: 'bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden',
} as const

// ============================================================
// フォーム要素
// ============================================================
export const input = {
  // テキスト入力
  base: 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500',
  // 検索入力（アイコン付き左パディング）
  search: 'w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500',
  // Select
  select: 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-800',
  // Textarea
  textarea: 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-50 dark:disabled:bg-gray-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500',
  // ラベル
  label: 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1',
  // エラーテキスト
  error: 'mt-1 text-xs text-red-600 dark:text-red-400',
  // ヘルプテキスト
  help: 'mt-1 text-xs text-gray-500 dark:text-gray-400',
} as const

// ============================================================
// バッジ
// ============================================================
export const badge = {
  base: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border',
  default: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600',
  success: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700',
  warning: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
  danger: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700',
  info: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700',
} as const

// ============================================================
// タイポグラフィ
// ============================================================
export const text = {
  // 見出し
  h1: 'text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100',
  h2: 'text-xl font-bold text-gray-900 dark:text-gray-100',
  h3: 'text-lg font-semibold text-gray-900 dark:text-gray-100',
  h4: 'text-base font-semibold text-gray-800 dark:text-gray-200',
  // 本文
  body: 'text-sm text-gray-600 dark:text-gray-400',
  bodyBase: 'text-base text-gray-600 dark:text-gray-400',
  // サブテキスト
  sub: 'text-xs text-gray-500 dark:text-gray-400',
  subSm: 'text-sm text-gray-500 dark:text-gray-400',
  // ラベル
  label: 'text-sm font-medium text-gray-700 dark:text-gray-300',
  labelXs: 'text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider',
  // リンク
  link: 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors',
  linkSm: 'text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors',
} as const

// ============================================================
// レイアウト
// ============================================================
export const layout = {
  // ページコンテナ
  page: 'min-h-screen bg-gray-50 dark:bg-gray-900',
  container: 'max-w-7xl mx-auto px-4 py-4 sm:py-8',
  containerSm: 'max-w-3xl mx-auto px-4 py-4 sm:py-8',
  // セクション間隔
  section: 'mb-6 sm:mb-8',
  sectionSm: 'mb-4 sm:mb-6',
  // フレックスユーティリティ
  flexBetween: 'flex items-center justify-between',
  flexCenter: 'flex items-center justify-center',
  flexStart: 'flex items-center gap-2',
  // グリッド
  grid2: 'grid grid-cols-1 sm:grid-cols-2 gap-4',
  grid3: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
  grid4: 'grid grid-cols-2 sm:grid-cols-4 gap-4',
} as const

// ============================================================
// テーブル
// ============================================================
export const table = {
  wrapper: 'overflow-x-auto',
  table: 'min-w-full divide-y divide-gray-200 dark:divide-gray-700',
  thead: 'bg-gray-50 dark:bg-gray-700',
  th: 'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider',
  tbody: 'bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700',
  td: 'px-6 py-4 text-sm text-gray-900 dark:text-gray-100',
  tdSub: 'px-6 py-4 text-sm text-gray-500 dark:text-gray-400',
  tr: 'hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
} as const

// ============================================================
// アラート・フィードバック
// ============================================================
export const alert = {
  base: 'flex items-start gap-3 p-4 rounded-lg border text-sm',
  error: 'flex items-start gap-3 p-4 rounded-lg border text-sm bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300',
  success: 'flex items-start gap-3 p-4 rounded-lg border text-sm bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300',
  warning: 'flex items-start gap-3 p-4 rounded-lg border text-sm bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300',
  info: 'flex items-start gap-3 p-4 rounded-lg border text-sm bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300',
} as const

// ============================================================
// スペーシング
// ============================================================
export const spacing = {
  // スタック（縦方向 space-y）
  stackXs: 'space-y-1',
  stackSm: 'space-y-2',
  stackMd: 'space-y-4',
  stackLg: 'space-y-6',
  // インライン（横方向 gap）
  inlineXs: 'gap-1',
  inlineSm: 'gap-2',
  inlineMd: 'gap-3',
  inlineLg: 'gap-4',
} as const

// ============================================================
// アニメーション
// ============================================================
export const transition = {
  colors: 'transition-colors',
  shadow: 'transition-shadow',
  all: 'transition-all',
  opacity: 'transition-opacity',
} as const
